import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiClientError, apiGet, apiPost } from '../lib/api';
import { browserTz } from '../lib/format';
import type { AuthMe } from '@shared/types';

interface AuthContextValue {
  status: 'loading' | 'authed' | 'guest';
  me: AuthMe | null;
  // IANA timezone to use when interpreting "today" for this user.
  // Prefers an explicit setting over the browser default — but treats
  // the legacy default 'America/New_York' as "not chosen" until a real
  // settings UI ships with a tzExplicit flag.
  effectiveTz: string;
  /**
   * Phase 14 §S1 — whether the current user has verified their email.
   * Mirrors `me.emailVerified` but resolved here so screens don't have
   * to repeat the "undefined → true" backward-compat coercion. False
   * means the SPA must keep the user on VerifyEmailNeededScreen until
   * they click the link in their inbox (which hits /api/auth/verify-email
   * and flips the flag server-side).
   */
  emailVerified: boolean;
  login: (email: string, password: string) => Promise<void>;
  // Phase 4 §legal — register now requires explicit Terms+Privacy
  // acceptance and a 18+ age attestation. RegisterScreen disables
  // submit until both checkboxes are checked; the server still
  // validates with z.literal(true) so a curl bypass can't sneak past.
  register: (
    email: string,
    password: string,
    flags: { termsAccepted: true; ageAttested: true },
  ) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'authed' | 'guest'>('loading');
  const [me, setMe] = useState<AuthMe | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await apiGet<AuthMe>('/api/auth/me');
      setMe(data);
      setStatus('authed');
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        setMe(null);
        setStatus('guest');
      } else {
        setStatus('guest');
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // When any API call (typically a mutation like POST /api/doses) hits
  // a session-killing 401, lib/api.ts dispatches 'auth:revoked'. Reset
  // to guest so the router sends the user back to login. Without this,
  // a warm PWA whose JWT got stale (idle_too_long, session_epoch bump)
  // leaves the user looking at a working UI but every save fails with
  // "Could not log — try again." — exactly the bug this fixes.
  useEffect(() => {
    function onRevoked() {
      setMe(null);
      setStatus('guest');
    }
    window.addEventListener('auth:revoked', onRevoked);
    return () => window.removeEventListener('auth:revoked', onRevoked);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      await apiPost('/api/auth/login', { email, password });
      await refresh();
    },
    [refresh],
  );

  const register = useCallback(
    async (
      email: string,
      password: string,
      flags: { termsAccepted: true; ageAttested: true },
    ) => {
      await apiPost('/api/auth/register', {
        email,
        password,
        termsAccepted: flags.termsAccepted,
        ageAttested: flags.ageAttested,
      });
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await apiPost('/api/auth/logout');
    } finally {
      setMe(null);
      setStatus('guest');
    }
  }, []);

  const effectiveTz = useMemo(() => {
    const saved = me?.settings.timezone;
    if (typeof saved === 'string' && saved && saved !== 'America/New_York') {
      return saved;
    }
    return browserTz();
  }, [me?.settings.timezone]);

  // Phase 14 §S1 — graceful degradation. A `me` payload missing the
  // `emailVerified` field (e.g. older server, mid-deploy) is treated as
  // verified so we don't accidentally bounce every user to the
  // verify-email screen during a rollout window where the server hasn't
  // shipped the field yet. The field becomes load-bearing only once
  // the server is known to populate it (post-deploy of S1).
  const emailVerified = me?.emailVerified !== false;

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, effectiveTz, emailVerified, login, register, logout, refresh }),
    [status, me, effectiveTz, emailVerified, login, register, logout, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
